import { useState } from "react";
import { toast } from "sonner";
import { useReveal } from "@/hooks/useReveal";
import { Download, FileText, Loader2 } from "lucide-react";
import { diagnosticPdfUrl, requestDiagnostic } from "@/lib/api";

const CHECKLIST = [
  "Dwell accumulation",
  "Trailer imbalance",
  "Empty repositioning",
  "Appointment discipline",
  "Vendor delay",
  "EDI event gaps",
  "Forecast volatility",
  "Premium freight",
  "Detention exposure",
  "Misclassified cost",
  "Data latency",
  "Cost-to-serve drift",
];

export default function LeadMagnet() {
  const { ref, visible } = useReveal();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter a work email to download the diagnostic.");
      return;
    }
    setLoading(true);
    try {
      await requestDiagnostic({ name, email, company });
      // Trigger download
      const link = document.createElement("a");
      link.href = diagnosticPdfUrl();
      link.download = "axiom-12-point-diagnostic.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Diagnostic sent. Your download is starting.");
      setSubmitted(true);
    } catch (err) {
      toast.error("Could not process your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="lead-magnet"
      ref={ref}
      data-testid="lead-magnet-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="absolute inset-0 bg-radial-fade opacity-60 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left: copy + form */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="num-label">SECTION 09 / DIAGNOSTIC</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <h2
              data-testid="lead-magnet-headline"
              className={`font-display font-bold text-axiom-text tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05] reveal ${
                visible ? "in" : ""
              }`}
            >
              Download the Axiom-AI 12-Point Hidden Supply Chain Cost Diagnostic.
            </h2>
            <p
              className={`mt-5 text-axiom-muted text-base sm:text-lg leading-relaxed max-w-2xl reveal ${
                visible ? "in" : ""
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              Use this diagnostic to identify where cost is moving through your
              network before it reaches the invoice. The checklist helps
              leadership teams examine dwell accumulation, trailer imbalance,
              empty repositioning, appointment discipline, vendor delay, EDI
              event gaps, forecast volatility, premium freight, detention
              exposure, misclassified cost, and data latency.
            </p>

            <form
              onSubmit={handleSubmit}
              data-testid="lead-magnet-form"
              className={`mt-8 max-w-xl grid grid-cols-1 sm:grid-cols-2 gap-3 reveal ${
                visible ? "in" : ""
              }`}
              style={{ transitionDelay: "180ms" }}
            >
              <input
                data-testid="lead-name"
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-axiom-surface border border-white/10 px-4 py-3 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none transition-colors"
              />
              <input
                data-testid="lead-company"
                type="text"
                placeholder="Company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="bg-axiom-surface border border-white/10 px-4 py-3 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none transition-colors"
              />
              <input
                data-testid="lead-email"
                type="email"
                required
                placeholder="Work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="sm:col-span-2 bg-axiom-surface border border-white/10 px-4 py-3 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none transition-colors"
              />
              <button
                type="submit"
                data-testid="lead-magnet-submit"
                disabled={loading}
                className="sm:col-span-2 btn-axiom justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Preparing...
                  </>
                ) : (
                  <>
                    <Download size={16} /> Get the Diagnostic
                  </>
                )}
              </button>
              {submitted && (
                <p
                  className="sm:col-span-2 text-xs text-axiom-accent font-mono"
                  data-testid="lead-magnet-success"
                >
                  ✓ DIAGNOSTIC.PDF DISPATCHED
                </p>
              )}
            </form>
          </div>

          {/* Right: document mock */}
          <div className="lg:col-span-5">
            <div
              data-testid="diagnostic-doc-mock"
              className={`relative aspect-[3/4] w-full max-w-md mx-auto border border-white/10 bg-axiom-surface p-6 sm:p-8 reveal ${
                visible ? "in" : ""
              }`}
              style={{
                transitionDelay: "240ms",
                boxShadow: "0 0 0 1px rgba(59,130,246,0.08), 0 30px 60px -30px rgba(59,130,246,0.25)",
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-axiom-accent" />
                  <span className="num-label">AXIOM-AI.DOC / DIAGNOSTIC</span>
                </div>
                <span className="num-label">v1.0</span>
              </div>

              <div className="mt-8">
                <div className="num-label">CHECKLIST · 12 POINTS</div>
                <h3 className="mt-2 font-display text-2xl text-axiom-text font-bold leading-tight">
                  Hidden Supply Chain Cost Diagnostic
                </h3>
              </div>

              <ul className="mt-6 space-y-2.5">
                {CHECKLIST.map((c, i) => (
                  <li
                    key={c}
                    className="flex items-center gap-3 text-sm text-axiom-muted border-b border-white/5 pb-2"
                  >
                    <span className="num-label w-6 text-axiom-accent">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{c}</span>
                    <div className="flex-1" />
                    <div className="w-3 h-3 border border-white/15" />
                  </li>
                ))}
              </ul>

              <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between num-label">
                <span>OPERATOR-GRADE</span>
                <span>AXIOM-AI</span>
              </div>

              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-4 h-4 border-l border-t border-axiom-accent/50" />
              <div className="absolute top-0 right-0 w-4 h-4 border-r border-t border-axiom-accent/50" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-l border-b border-axiom-accent/50" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-r border-b border-axiom-accent/50" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
